'use client';

import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface SubmissionSummary {
  month: string;
  centre: string;
  submittedAt: string;
  referenceId: string;
  projects: {
    kalpTaruhTrees: string;
    pvParticipants: string;
    pvChampion1Name: string;
    pvChampion2Name: string;
    pvChampion3Name: string;
    gokulVillageName: string;
  };
  activities: {
    sakashAmritvela: string;
    sakashNumaShaam: string;
    awarenessJeevan: string;
    awarenessVidyarthi: string;
    walksCount: string;
    cleanupCount: string;
    competitionType: string;
    workshopCount: string;
  };
  habitScore: number;
}

interface SubmissionConfirmationProps {
  summary: SubmissionSummary;
  onLogAnother: () => void;
}

const nextSteps = [
  {
    icon: 'EyeIcon',
    title: 'State Coordinator Review',
    description: 'Your state coordinator will review the submitted log within 2–3 working days.',
    color: 'bg-primary/10 text-primary',
  },
  {
    icon: 'BellAlertIcon',
    title: 'Verification Notification',
    description: 'You will receive a notification once your log is verified or if any clarification is needed.',
    color: 'bg-[var(--info-bg)] text-[var(--info)]',
  },
  {
    icon: 'PencilSquareIcon',
    title: 'Edits Still Allowed',
    description: 'You can still edit this log until it is marked as verified by your coordinator.',
    color: 'bg-[var(--warning-bg)] text-[var(--warning)]',
  },
  {
    icon: 'ChartBarIcon',
    title: 'Dashboard Updated',
    description: 'Your centre\'s data will reflect on the coordinator dashboard after verification.',
    color: 'bg-[var(--success-bg)] text-[var(--success)]',
  },
];

export default function SubmissionConfirmation({ summary, onLogAnother }: SubmissionConfirmationProps) {
  const hasProjects =
    summary.projects.kalpTaruhTrees ||
    summary.projects.pvParticipants ||
    summary.projects.gokulVillageName;

  const hasActivities =
    summary.activities.sakashAmritvela ||
    summary.activities.awarenessJeevan ||
    summary.activities.walksCount ||
    summary.activities.cleanupCount ||
    summary.activities.workshopCount;

  return (
    <div className="space-y-6 pb-10 animate-fade-in">
      {/* Success hero */}
      <div className="bg-card rounded-2xl border border-[var(--success)]/30 shadow-card p-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-[var(--success-bg)] flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircleIcon" size={36} variant="outline" className="text-[var(--success)]" />
        </div>
        <h1 className="text-2xl font-extrabold text-foreground mb-1">Activity Log Submitted!</h1>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Your monthly activity report for <strong className="text-foreground">{summary.month}</strong> has been successfully submitted to your state coordinator.
        </p>
        <div className="flex items-center justify-center gap-4 mt-4 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted text-muted-foreground text-xs font-semibold">
            <Icon name="BuildingOfficeIcon" size={13} variant="outline" />
            {summary.centre}
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted text-muted-foreground text-xs font-semibold">
            <Icon name="ClockIcon" size={13} variant="outline" />
            {summary.submittedAt}
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold">
            <Icon name="HashtagIcon" size={13} variant="outline" />
            Ref: {summary.referenceId}
          </span>
        </div>
      </div>

      {/* Submitted data summary */}
      <div className="bg-card rounded-2xl border border-border shadow-card overflow-hidden">
        <div className="flex items-center gap-3 p-5 border-b border-border">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
            <Icon name="ClipboardDocumentCheckIcon" size={18} variant="outline" className="text-primary" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-foreground">Submitted Data Summary</h2>
            <p className="text-xs text-muted-foreground">Overview of what was reported for {summary.month}</p>
          </div>
        </div>

        <div className="divide-y divide-border">
          {/* Projects */}
          {hasProjects && (
            <div className="p-5 space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="FolderOpenIcon" size={15} variant="outline" className="text-primary" />
                <h3 className="text-xs font-bold text-foreground uppercase tracking-wider">Projects</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {summary.projects.kalpTaruhTrees && (
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-muted/40">
                    <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="GlobeAltIcon" size={14} variant="outline" className="text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Kalp Taruh — Trees Planted</p>
                      <p className="text-sm font-bold text-foreground">{summary.projects.kalpTaruhTrees} trees 🌳</p>
                    </div>
                  </div>
                )}
                {summary.projects.pvParticipants && (
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-muted/40">
                    <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                      <Icon name="TrophyIcon" size={14} variant="outline" className="text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Prakriti Veer — Participants Observed</p>
                      <p className="text-sm font-bold text-foreground">{summary.projects.pvParticipants} members</p>
                    </div>
                  </div>
                )}
                {(summary.projects.pvChampion1Name || summary.projects.pvChampion2Name || summary.projects.pvChampion3Name) && (
                  <div className="sm:col-span-2 p-3 rounded-xl bg-amber-50 border border-amber-200">
                    <p className="text-xs font-semibold text-amber-700 mb-2">🏆 Prakriti Veer Champions</p>
                    <div className="flex flex-wrap gap-2">
                      {summary.projects.pvChampion1Name && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-400/20 text-amber-800 text-xs font-semibold">
                          🥇 {summary.projects.pvChampion1Name}
                        </span>
                      )}
                      {summary.projects.pvChampion2Name && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-200 text-gray-700 text-xs font-semibold">
                          🥈 {summary.projects.pvChampion2Name}
                        </span>
                      )}
                      {summary.projects.pvChampion3Name && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold">
                          🥉 {summary.projects.pvChampion3Name}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {summary.projects.gokulVillageName && (
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-muted/40">
                    <div className="w-7 h-7 rounded-lg bg-[var(--success-bg)] flex items-center justify-center flex-shrink-0">
                      <Icon name="HomeModernIcon" size={14} variant="outline" className="text-[var(--success)]" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Gokul Gram — Model Village</p>
                      <p className="text-sm font-bold text-foreground">{summary.projects.gokulVillageName}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Activities */}
          {hasActivities && (
            <div className="p-5 space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="ClipboardDocumentListIcon" size={15} variant="outline" className="text-secondary" />
                <h3 className="text-xs font-bold text-foreground uppercase tracking-wider">Activities</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {summary.activities.sakashAmritvela && (
                  <div className="p-3 rounded-xl bg-secondary/5 border border-secondary/20 text-center">
                    <p className="text-xl font-extrabold text-secondary tabular-nums">{summary.activities.sakashAmritvela}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Amritvela Sakash days 🧘‍♀️</p>
                  </div>
                )}
                {summary.activities.sakashNumaShaam && (
                  <div className="p-3 rounded-xl bg-secondary/5 border border-secondary/20 text-center">
                    <p className="text-xl font-extrabold text-secondary tabular-nums">{summary.activities.sakashNumaShaam}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Numa Shaam Sakash days</p>
                  </div>
                )}
                {summary.activities.awarenessJeevan && summary.activities.awarenessJeevan !== '0' && (
                  <div className="p-3 rounded-xl bg-[var(--info-bg)] border border-[var(--info)]/20 text-center">
                    <p className="text-xl font-extrabold text-[var(--info)] tabular-nums">{summary.activities.awarenessJeevan}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Awareness sessions 📝</p>
                  </div>
                )}
                {summary.activities.awarenessVidyarthi && summary.activities.awarenessVidyarthi !== '0' && (
                  <div className="p-3 rounded-xl bg-[var(--info-bg)] border border-[var(--info)]/20 text-center">
                    <p className="text-xl font-extrabold text-[var(--info)] tabular-nums">{summary.activities.awarenessVidyarthi}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Vidyarthi sessions</p>
                  </div>
                )}
                {summary.activities.walksCount && summary.activities.walksCount !== '0' && (
                  <div className="p-3 rounded-xl bg-muted/40 border border-border text-center">
                    <p className="text-xl font-extrabold text-foreground tabular-nums">{summary.activities.walksCount}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Nature walks 🚶‍♂️</p>
                  </div>
                )}
                {summary.activities.cleanupCount && summary.activities.cleanupCount !== '0' && (
                  <div className="p-3 rounded-xl bg-muted/40 border border-border text-center">
                    <p className="text-xl font-extrabold text-foreground tabular-nums">{summary.activities.cleanupCount}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Community clean-ups</p>
                  </div>
                )}
                {summary.activities.workshopCount && summary.activities.workshopCount !== '0' && (
                  <div className="p-3 rounded-xl bg-muted/40 border border-border text-center">
                    <p className="text-xl font-extrabold text-foreground tabular-nums">{summary.activities.workshopCount}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Workshops 🌍</p>
                  </div>
                )}
                {summary.activities.competitionType && (
                  <div className="p-3 rounded-xl bg-muted/40 border border-border text-center col-span-2 sm:col-span-1">
                    <p className="text-xs font-bold text-foreground">🎨 Competition</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug">{summary.activities.competitionType}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Habits */}
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="HeartIcon" size={15} variant="outline" className="text-[var(--danger)]" />
              <h3 className="text-xs font-bold text-foreground uppercase tracking-wider">Habit Adoption</h3>
            </div>
            <div className="flex items-center gap-4 p-4 rounded-xl bg-primary/5 border border-primary/20">
              <div className="text-center flex-shrink-0">
                <p className="text-3xl font-extrabold text-primary tabular-nums">{summary.habitScore}%</p>
                <p className="text-[10px] text-muted-foreground">Overall Score</p>
              </div>
              <div className="flex-1">
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className="h-3 rounded-full bg-primary transition-all duration-700"
                    style={{ width: `${summary.habitScore}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  {summary.habitScore >= 75
                    ? '🌟 Excellent adoption — your centre is leading by example!'
                    : summary.habitScore >= 50
                    ? '👍 Good progress — keep encouraging members to adopt more habits.'
                    : summary.habitScore >= 25
                    ? '🌱 Growing — focus on 2–3 key habits to build momentum.'
                    : '🔔 Just getting started — every small step counts!'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Next steps for coordinators */}
      <div className="bg-card rounded-2xl border border-border shadow-card overflow-hidden">
        <div className="flex items-center gap-3 p-5 border-b border-border">
          <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
            <Icon name="ArrowRightCircleIcon" size={18} variant="outline" className="text-amber-600" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-foreground">What Happens Next</h2>
            <p className="text-xs text-muted-foreground">Steps after your submission</p>
          </div>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {nextSteps.map((step, idx) => (
            <div key={`step-${idx}`} className="flex items-start gap-3 p-4 rounded-xl border border-border bg-muted/20">
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${step.color}`}>
                <Icon name={step.icon as any} size={16} variant="outline" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">{step.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <Link href="/" className="btn-secondary flex-1 justify-center text-sm py-2.5">
          <Icon name="ChartBarIcon" size={16} variant="outline" />
          View Dashboard
        </Link>
        <button
          type="button"
          onClick={onLogAnother}
          className="btn-primary flex-1 justify-center text-sm py-2.5"
        >
          <Icon name="PlusCircleIcon" size={16} variant="outline" />
          Log Another Month
        </button>
      </div>
    </div>
  );
}
